import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ErrorResponse } from '@sparkai/utils'

import { apiClient } from '../../apiClient'
import { PROGRAM_GROUP_DETAIL_KEY } from '../../program-groups/useProgramGroups'
import { PROGRAM_AUDIT_KEY, type ProgramBenchmarkSuperMajorityConfig } from '../useAudit'
import { PROGRAM_BY_ID_KEY } from '../useProgram'

export type ProgramRecyclerConfigResponse = {
  enabled: boolean
  recyclerProgramId: string | null
  startDate: string | null
  endDate: string | null
  movingEndDate: number | null
  samplingConditions: []
  engagementCount: number | null
  schedule: string | null
}

type EnabledBenchmarkRecyclerConfigRequest = {
  enabled: true
  startDate: string
  endDate: string | null
  movingEndDate: number | null
  samplingConditions: []
  engagementCount: number
  schedule: string
}

type DisabledBenchmarkRecyclerConfigRequest = {
  enabled: false
}

export type ProgramBenchmarkAuditQueueConfig = {
  enabled: boolean
  type?: 'rate' | 'weighted'
  perMsRate?: number
  perMsMaxCount?: number
}

export type ProgramBenchmarkSuperMajorityConfigRequest = {
  enabled: boolean
  minimumTotalVotes: number
  agreementRatio: number
  maximumTotalVotes: number
}

export type UpdateProgramBenchmarkConfigRequest = {
  programId: string
  auditConsensus: boolean
  auditQueue?: ProgramBenchmarkAuditQueueConfig
  recycler?: EnabledBenchmarkRecyclerConfigRequest | DisabledBenchmarkRecyclerConfigRequest
  superMajority?: ProgramBenchmarkSuperMajorityConfigRequest
}

type UpdateProgramBenchmarkConfigResponse = {
  auditConsensus: boolean
  auditQueue?: ProgramBenchmarkAuditQueueConfig
  recycler?: ProgramRecyclerConfigResponse
  superMajority?: ProgramBenchmarkSuperMajorityConfig
}

export const PROGRAM_RECYCLER_CONFIG_KEY = 'program-recycler-config'

export function useGetByLiveProgramId(liveProgramId?: string) {
  return useQuery<ProgramRecyclerConfigResponse, ErrorResponse>({
    queryKey: [PROGRAM_RECYCLER_CONFIG_KEY, liveProgramId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramRecyclerConfigResponse>(
        `/int/v3/programs/${liveProgramId}/recycler`
      )
      return data
    },
    enabled: Boolean(liveProgramId),
  })
}

export function useUpdateBenchmarkConfig() {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateProgramBenchmarkConfigResponse,
    ErrorResponse,
    UpdateProgramBenchmarkConfigRequest
  >({
    mutationFn: async ({ programId, ...payload }) => {
      const { data } = await apiClient.put<UpdateProgramBenchmarkConfigResponse>(
        `/int/v3/programs/${programId}/config/benchmark`,
        payload
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_GROUP_DETAIL_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_RECYCLER_CONFIG_KEY, variables.programId] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_AUDIT_KEY, variables.programId] })
      queryClient.invalidateQueries({ queryKey: [PROGRAM_BY_ID_KEY, variables.programId] })
    },
  })
}
